import React, { useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import QrScanner from 'qr-scanner';
import { validateSKReceiptQR } from '../../utils/qrValidation';
import { trackEvent } from '../../utils/posthog';
import { getAuthToken } from '../../utils/authToken';
import { config } from '../../config';

interface PhotoUploadFlowProps {
  instance: PublicClientApplication;
  onReceiptProcessed: (receiptData: any) => void;
  onBackToScanner: () => void;
  initialMode?: 'photo-upload' | 'manual-uid';  // Allow parent to specify which mode to start in
}

function PhotoUploadFlow({ instance, onReceiptProcessed, onBackToScanner, initialMode = 'photo-upload' }: PhotoUploadFlowProps) {
  // View mode: 'photo-upload' or 'manual-uid'
  const [viewMode, setViewMode] = useState<'photo-upload' | 'manual-uid'>(initialMode);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [manualUid, setManualUid] = useState('');
  const [moreBills, setMoreBills] = useState('no');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSuccess, setProcessingSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Helper to show error messages
  const showError = (message: string) => {
    setErrorMessage(message);
    setStatusMessage('');
    setIsProcessing(false);
    // Auto-hide after 8 seconds
    setTimeout(() => setErrorMessage(''), 8000);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  // Process receipt UID (validation + storage)
  const processReceiptUID = async (uid: string) => {
    console.log('[processReceiptUID] Starting with UID:', uid);
    setIsProcessing(true);
    setProcessingSuccess(false);
    setStatusMessage('🔄 Validujem bloček s eKasa...');
    setErrorMessage('');

    try {
      console.log('[processReceiptUID] Getting auth token...');
      setStatusMessage('🔑 Získavam autentifikáciu...');
      const token = await getAuthToken(instance);
      console.log('[processReceiptUID] Auth token received:', token ? 'YES' : 'NO');
      if (!token) {
        throw new Error('Chyba autentifikácie - skúste sa odhlásiť a prihlásiť');
      }

      console.log('[processReceiptUID] Calling getReceiptFromeKasa...');
      setStatusMessage('📡 Overujem bloček na eKasa...');

      // Generate uploadId for QR scan (backend requires it)
      const uploadId = `QR_SCAN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Show progress for long-running requests
      const progressTimer = setTimeout(() => {
        setStatusMessage('⏳ eKasa server je pomalý, čakám...');
      }, 10000); // After 10 seconds

      const slowTimer = setTimeout(() => {
        setStatusMessage('⏳ Stále pracujem, môže to trvať až minútu...');
      }, 30000); // After 30 seconds

      let response;
      try {
        response = await fetch(`${config.apiUrl}/getReceiptFromeKasa`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': `Bearer ${token}`
          },
          body: JSON.stringify({
            uploadId: uploadId,
            manualUid: uid
          })
        });
      } catch (networkError: any) {
        clearTimeout(progressTimer);
        clearTimeout(slowTimer);
        throw new Error('Problém s pripojením na server. Skontrolujte internet.');
      } finally {
        clearTimeout(progressTimer);
        clearTimeout(slowTimer);
      }

      console.log('[processReceiptUID] Response status:', response.status);

      if (response.status === 422) {
        const htmlContent = await response.text();
        document.open();
        document.write(htmlContent);
        document.close();
        return;
      }

      if (!response.ok) {
        let errorMsg = `Server error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMsg);
      }

      const validationResult = await response.json();
      console.log('[processReceiptUID] Validation successful:', validationResult);
      setStatusMessage('✅ Bloček overený!');
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause to show success

      // Store receipt
      setStatusMessage('💾 Ukladám bloček do databázy...');
      console.log('[processReceiptUID] Calling storeReceiptOnly...');
      console.log('[processReceiptUID] moreBills state:', moreBills);

      // Use the same uploadId from validation
      let storeResponse;
      try {
        storeResponse = await fetch(`${config.apiUrl}/storeReceiptOnly`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': `Bearer ${token}`
          },
          body: JSON.stringify({
            uploadId: uploadId,
            validationResult: validationResult,  // ✅ FIX: Changed from receiptData to validationResult
            action: 'store',
            moreBills: moreBills
          })
        });
      } catch (networkError: any) {
        throw new Error('Problém s pripojením pri ukladaní. Skontrolujte internet.');
      }

      console.log('[processReceiptUID] Store response status:', storeResponse.status);

      // Handle duplicate receipt error
      if (storeResponse.status === 409) {
        const errorData = await storeResponse.json();
        console.log('[processReceiptUID] Duplicate receipt detected:', errorData);

        setStatusMessage('');
        setIsProcessing(false);

        // Show duplicate receipt error
        onReceiptProcessed({
          isDuplicate: true,
          receipt_hash: errorData.receipt_id,
          message: errorData.user_message || 'Tento bloček je už uložený.',
          action: errorData.action || 'scan_new_receipt'
        });
        return;
      }

      if (!storeResponse.ok) {
        let errorMsg = `Server error pri ukladaní (${storeResponse.status})`;
        try {
          const errorData = await storeResponse.json();
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          // Response wasn't JSON
        }
        throw new Error(errorMsg);
      }

      const storeResult = await storeResponse.json();
      console.log('[processReceiptUID] Receipt stored successfully:', storeResult);
      setStatusMessage('✅ Bloček uložený!');
      await new Promise(resolve => setTimeout(resolve, 300)); // Brief pause to show success

      // ✅ FIX: Backend returns { receipt_data: {...} }, not flat object
      const receiptData = storeResult.receipt_data || storeResult;
      setProcessingSuccess(true);

      // Pass receipt data to parent
      onReceiptProcessed(receiptData);

      // Track successful receipt upload
      trackEvent('receipt_uploaded', {
        source: 'sk',
        input_method: viewMode === 'photo-upload' ? 'photo_upload' : 'manual_uid',
        merchant: receiptData.merchant || 'Unknown',
        total_amount: receiptData.total_gross || 0,
        currency: receiptData.currency || 'EUR',
        items_count: receiptData.items?.length || 0
      });

      // Reset form if not uploading more bills
      if (moreBills === 'no') {
        console.log('[processReceiptUID] Resetting to photo upload view');
        setViewMode('photo-upload');
        setManualUid('');
        setSelectedFile(null);
      } else {
        console.log('[processReceiptUID] More bills mode - staying on current view');
      }
    } catch (error: any) {
      console.error('[processReceiptUID] Error caught:', error);

      // Track upload failure
      trackEvent('receipt_upload_failed', {
        input_method: viewMode === 'photo-upload' ? 'photo_upload' : 'manual_uid',
        error_message: error.message || 'Unknown error'
      });

      showError(`Chyba: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setStatusMessage('');
      console.log('[processReceiptUID] Finished');
    }
  };

  // Handle photo upload with client-side QR extraction
  const handlePhotoUploadSubmit = async (file: File) => {
    setIsProcessing(true);
    setStatusMessage('Analyzujem fotku...');

    try {
      // Try client-side QR extraction first
      try {
        const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
        const validation = validateSKReceiptQR(result.data);

        if (validation.valid) {
          // Success! Skip upload, process UID directly
          await processReceiptUID(validation.uid);
          return;
        }
      } catch (qrError) {
        console.log('Client-side QR scan failed, falling back to backend...', qrError);
      }

      // Fallback: Upload to backend for OpenAI Vision analysis
      setStatusMessage('Nahrávam fotku na server...');
      const token = await getAuthToken(instance);
      if (!token) {
        throw new Error('Authentication required');
      }

      const fileExtension = file.name.split('.').pop() || '';
      const uploadResponse = await fetch(`${config.apiUrl}/uploadReceipt`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'X-File-Extension': fileExtension,
          'X-Auth-Token': `Bearer ${token}`
        },
        body: file
      });

      if (!uploadResponse.ok) {
        // Parse backend error message for better user feedback
        let errorMessage = 'Nepodarilo sa nahrať súbor';
        try {
          const errorData = await uploadResponse.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If JSON parsing fails, use generic error
        }
        throw new Error(errorMessage);
      }

      const uploadResult = await uploadResponse.json();

      // Analyze with backend
      setStatusMessage('Analyzujem bloček...');
      const analyzeResponse = await fetch(`${config.apiUrl}/analyzeSKReceiptImage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({
          uploadId: uploadResult.uploadId,
          blobUrl: uploadResult.blobUrl
        })
      });

      if (analyzeResponse.status === 422) {
        // Parse error response safely instead of rendering HTML directly
        try {
          const errorData = await analyzeResponse.json();
          showError(errorData.message || 'Bloček nemohol byť spracovaný');
        } catch {
          // If JSON parsing fails, show generic error
          showError('Bloček nemohol byť spracovaný. Skontrolujte QR kód.');
        }
        return;
      }

      if (!analyzeResponse.ok) {
        const errorData = await analyzeResponse.json();
        throw new Error(errorData.message || 'Failed to analyze image');
      }

      const analyzeResult = await analyzeResponse.json();

      // Process the extracted UID
      await processReceiptUID(analyzeResult.qrCode);
    } catch (error: any) {
      // Track upload failure (photo upload)
      trackEvent('receipt_upload_failed', {
        input_method: 'photo_upload',
        error_message: error.message || 'Unknown error'
      });

      showError(`Chyba: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setStatusMessage('');
    }
  };

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBackToScanner}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#667eea',
          fontSize: '16px',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: '16px',
          padding: '8px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        ← Späť na skener
      </button>

      {/* Photo Upload View */}
      {viewMode === 'photo-upload' && (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (selectedFile) {
            handlePhotoUploadSubmit(selectedFile);
          } else {
            showError('Vyberte súbor');
          }
        }}>
          {/* File Upload */}
          <div style={{ margin: '20px 0' }}>
            <div
              style={{
                border: '2px dashed #e5e7eb',
                borderRadius: '12px',
                padding: 'clamp(18px, 5vw, 28px)',
                textAlign: 'center',
                background: '#f9fafb',
                cursor: 'pointer',
                transition: 'border-color .2s, background .2s',
                borderColor: selectedFile ? '#10b981' : '#e5e7eb',
                backgroundColor: selectedFile ? '#f0fdf4' : '#f9fafb'
              }}
              onClick={() => document.getElementById('fileInput')?.click()}
            >
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                aria-label="Nahrať obrázok bločku"
              />
              {!selectedFile && (
                <svg style={{ width: '44px', height: '44px', margin: '0 auto 8px', stroke: '#9ca3af' }}
                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M7 16a 4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
              )}
              <div style={{
                color: selectedFile ? '#065f46' : '#4b5563',
                fontSize: 'clamp(16px, 4.5vw, 18px)',
                fontWeight: selectedFile ? 700 : 'normal'
              }}>
                {selectedFile ? `✅ ${selectedFile.name}` : (
                  <>
                    Kliknite pre výber súboru
                    <small style={{ display: 'block', color: '#9ca3af', fontSize: '14px', marginTop: '4px' }}>
                      Fotogaléria • Fotoaparát • Súbory
                    </small>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Status messages for photo upload */}
          {isProcessing && (
            <div style={{
              background: '#eef2ff',
              border: '1px solid #667eea',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#667eea', fontSize: '16px', fontWeight: 600 }}>
                {statusMessage}
              </div>
            </div>
          )}

          {errorMessage && (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #ef4444',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {errorMessage}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isProcessing || !selectedFile}
            style={{
              width: '100%',
              minHeight: '52px',
              padding: '14px 16px',
              background: (isProcessing || !selectedFile) ? '#cbd5e1' : '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '19px',
              fontWeight: 700,
              cursor: (isProcessing || !selectedFile) ? 'not-allowed' : 'pointer',
              marginTop: '16px'
            }}
          >
            {isProcessing ? 'Spracovávam...' : '📄 Spracovať bloček'}
          </button>
        </form>
      )}

      {/* Manual UID Input View */}
      {viewMode === 'manual-uid' && (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (manualUid.trim()) {
            const validation = validateSKReceiptQR(manualUid.trim());
            if (validation.valid) {
              processReceiptUID(validation.uid);
            } else {
              showError('Neplatný formát UID. Skontrolujte zadaný kód.');
            }
          } else {
            showError('Zadajte UID bločku');
          }
        }}>
          {/* Manual UID input */}
          <div style={{ margin: '20px 0' }}>
            <div style={{
              border: '2px solid #667eea',
              borderRadius: '12px',
              padding: 'clamp(16px, 4.5vw, 22px)',
              background: '#eef2ff'
            }}>
              <svg style={{ width: '40px', height: '40px', stroke: '#667eea', margin: '0 auto 10px', display: 'block' }}
                   xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', lineHeight: '1.4' }}>
                Zadaj ID bločka (niekedy označené ako UID). Prvý znak je zvyčajne písmeno O- (nie číslica 0), no môže byť aj iné písmeno napr V-
              </div>
              <input
                type="text"
                value={manualUid}
                onChange={(e) => setManualUid(e.target.value)}
                placeholder="O-89E5AA494F2245B8..."
                maxLength={50}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  background: '#fff',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Status messages for manual UID */}
          {isProcessing && (
            <div style={{
              background: '#eef2ff',
              border: '1px solid #667eea',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#667eea', fontSize: '16px', fontWeight: 600 }}>
                {statusMessage}
              </div>
            </div>
          )}

          {errorMessage && (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #ef4444',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {errorMessage}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isProcessing || !manualUid.trim()}
            style={{
              width: '100%',
              minHeight: '52px',
              padding: '14px 16px',
              background: (isProcessing || !manualUid.trim()) ? '#cbd5e1' : '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '19px',
              fontWeight: 700,
              cursor: (isProcessing || !manualUid.trim()) ? 'not-allowed' : 'pointer'
            }}
          >
            {isProcessing ? 'Spracovávam...' : '🔤 Spracovať UID'}
          </button>
        </form>
      )}

      {/* More Bills Toggle */}
      <div style={{ margin: '20px 0' }}>
        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '10px 12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}>
            <span style={{
              fontSize: '13px',
              color: '#374151',
              fontWeight: 500
            }}>
              Budeš nahrávať viac bločkov naraz?
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <div
                style={{
                  position: 'relative',
                  width: '56px',
                  height: '32px',
                  background: moreBills === 'yes' ? '#667eea' : '#d1d5db',
                  borderRadius: '16px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  transition: 'background .25s ease',
                  outline: 'none',
                  opacity: isProcessing ? 0.5 : 1
                }}
                onClick={() => !isProcessing && setMoreBills(moreBills === 'no' ? 'yes' : 'no')}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: moreBills === 'yes' ? '26px' : '2px',
                  width: '28px',
                  height: '28px',
                  background: '#fff',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,.15)',
                  transition: 'left .25s ease'
                }} />
              </div>
              <span style={{
                fontSize: '13px',
                color: moreBills === 'yes' ? '#667eea' : '#9ca3af',
                fontWeight: moreBills === 'yes' ? 700 : 500,
                transition: 'color .2s, font-weight .2s',
                whiteSpace: 'nowrap'
              }}>
                {moreBills === 'yes' ? 'Áno' : 'Nie'}
              </span>
            </div>
          </div>
        </div>
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '6px',
          textAlign: 'center',
          lineHeight: '1.3',
          padding: '0 4px'
        }}>
          {moreBills === 'yes'
            ? '✅ Rýchlejšie nascanuješ a uložíš viac bločkov. Kategorizácia sa spustí neskôr a spracuje všetky položky.'
            : 'ℹ️ Hneď po nahratí bločka sa spustí kategorizácia položiek'}
        </div>
      </div>
    </div>
  );
}

export default PhotoUploadFlow;
